import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './services/AuthContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminLayout from './components/layout/AdminLayout';
import InterviewerLayout from './components/layout/InterviewerLayout';
import CandidateLayout from './components/layout/CandidateLayout';
import RequireOrganization from './components/RequireOrganization';
import LoadingSpinner from './components/common/LoadingSpinner';

// Lazy load page components for code splitting
const HomePage = React.lazy(() => import('./pages/Home'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const CandidateTasksDashboard = React.lazy(() => import('./pages/CandidateTasksDashboard'));
const InterviewerDashboard = React.lazy(() => import('./pages/InterviewerDashboard'));
const TaskDetail = React.lazy(() => import('./pages/TaskDetail'));
const CodeEditor = React.lazy(() => import('./pages/CodeEditor'));
const EnterpriseSupportPortal = React.lazy(() => import('./pages/Support'));
const DocumentationPortalHome = React.lazy(() => import('./pages/DocumentationPortalHome'));
const DocumentationFileViewer = React.lazy(() => import('./pages/DocumentationFileViewer'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const UserTypeSelection = React.lazy(() => import('./pages/UserTypeSelection'));
const ProfileSetup = React.lazy(() => import('./pages/ProfileSetup'));
const SubscriptionPlans = React.lazy(() => import('./pages/SubscriptionPlans'));
const Unauthorized = React.lazy(() => import('./pages/Unauthorized'));
const Callback = React.lazy(() => import('./pages/Callback'));
const CreateTemplate = React.lazy(() => import('./pages/CreateTemplate'));
const TemplateList = React.lazy(() => import('./pages/TemplateList'));
const CandidateDirectory = React.lazy(() => import('./pages/CandidateDirectory'));
const OrgAdminDashboard = React.lazy(() => import('./pages/OrgAdminDashboard'));
const SuperAdminDashboard = React.lazy(() => import('./pages/SuperAdminDashboard'));
const OrganizationForm = React.lazy(() => import('./pages/OrganizationForm'));
const OrgAdminUsers = React.lazy(() => import('./pages/OrgAdminUsers'));
const OrgAdminSettings = React.lazy(() => import('./pages/OrgAdminSettings'));
const OrgAdminTasks = React.lazy(() => import('./pages/OrgAdminTasks'));
const InvitationAccept = React.lazy(() => import('./pages/InvitationAccept'));
const CreateTask = React.lazy(() => import('./pages/CreateTask'));
const CandidatesManagement = React.lazy(() => import('./pages/CandidatesManagement'));
const InterviewersManagement = React.lazy(() => import('./pages/InterviewersManagement'));
const SubscriptionManagement = React.lazy(() => import('./pages/SubscriptionManagement'));
const Dashboard = React.lazy(() => import('./components/admin/Dashboard'));
const AnalyticsDashboard = React.lazy(() => import('./components/admin/AnalyticsDashboard').then(module => ({ default: module.AnalyticsDashboard })));
const Features = React.lazy(() => import('./pages/static').then(module => ({ default: module.Features })));
const Integrations = React.lazy(() => import('./pages/static').then(module => ({ default: module.Integrations })));
const Docs = React.lazy(() => import('./pages/static').then(module => ({ default: module.Docs })));
const Roadmap = React.lazy(() => import('./pages/static').then(module => ({ default: module.Roadmap })));
const About = React.lazy(() => import('./pages/static').then(module => ({ default: module.About })));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const BillingAccount = React.lazy(() => import('./pages/BillingAccount'));
const CheckoutSuccess = React.lazy(() => import('./pages/CheckoutSuccess'));
const DebugAuth = React.lazy(() => import('./pages/DebugAuth'));
const DashboardRedirect = React.lazy(() => import('./pages/DashboardRedirect'));

// Lazy load new feature pages
const GamificationPage = React.lazy(() => import('./pages/Gamification'));
const LearningPage = React.lazy(() => import('./pages/Learning'));
const BehavioralAnalysisPage = React.lazy(() => import('./pages/BehavioralAnalysis'));
const SimulationsPage = React.lazy(() => import('./pages/Simulations'));
const GameModePage = React.lazy(() => import('./pages/GameMode'));
const Settings = React.lazy(() => import('./pages/Settings'));

// Lazy load legal and support pages
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const SupportDocs = React.lazy(() => import('./pages/SupportDocs'));

// Wrapper component for lazy-loaded routes
const LazyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<LoadingSpinner message="Loading page..." />}>
    {children}
  </Suspense>
);

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={
        isAuthenticated ? <Navigate to="/dashboard" /> : (
          <LazyRoute>
            <HomePage />
          </LazyRoute>
        )
      } />
      <Route path="/pricing" element={
        <LazyRoute>
          <Pricing />
        </LazyRoute>
      } />
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/dashboard" /> : (
          <LazyRoute>
            <Login />
          </LazyRoute>
        )
      } />
      <Route path="/register" element={
        isAuthenticated ? <Navigate to="/dashboard" /> : (
          <LazyRoute>
            <Register />
          </LazyRoute>
        )
      } />
      <Route path="/callback" element={
        <LazyRoute>
          <Callback />
        </LazyRoute>
      } />
      <Route path="/checkout/success" element={
        <LazyRoute>
          <CheckoutSuccess />
        </LazyRoute>
      } />
      <Route path="/debug-auth" element={
        <LazyRoute>
          <DebugAuth />
        </LazyRoute>
      } />
      <Route path="/unauthorized" element={
        <LazyRoute>
          <Unauthorized />
        </LazyRoute>
      } />
      <Route path="/invitation" element={
        <LazyRoute>
          <InvitationAccept />
        </LazyRoute>
      } />

      {/* Onboarding Routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/user-type-selection" element={
          <LazyRoute>
            <UserTypeSelection />
          </LazyRoute>
        } />
        <Route path="/profile-setup" element={
          <LazyRoute>
            <ProfileSetup />
          </LazyRoute>
        } />
        <Route path="/subscription-plans" element={
          <LazyRoute>
            <SubscriptionPlans />
          </LazyRoute>
        } />
        <Route path="/organization-form" element={
          <LazyRoute>
            <OrganizationForm />
          </LazyRoute>
        } />
        <Route path="/dashboard" element={
          <LazyRoute>
            <DashboardRedirect />
          </LazyRoute>
        } />
      </Route>

      {/* Protected Candidate Routes */}
      <Route element={<ProtectedRoute allowedRoles={['candidate']} />}>
        <Route element={<CandidateLayout />}>
          <Route path="/candidate/dashboard" element={
            <LazyRoute>
              <CandidateTasksDashboard />
            </LazyRoute>
          } />
          <Route path="/task/:taskId" element={
            <LazyRoute>
              <TaskDetail />
            </LazyRoute>
          } />
          <Route path="/candidate/tasks/:taskId" element={
            <LazyRoute>
              <TaskDetail />
            </LazyRoute>
          } />
          <Route path="/code-editor/:taskId" element={
            <LazyRoute>
              <CodeEditor />
            </LazyRoute>
          } />
          <Route path="/behavioral-analysis" element={
            <LazyRoute>
              <BehavioralAnalysisPage />
            </LazyRoute>
          } />
          <Route path="/candidate/game" element={
            <LazyRoute>
              <GameModePage />
            </LazyRoute>
          } />
          <Route path="/candidate/game/:taskId" element={
            <LazyRoute>
              <TaskDetail />
            </LazyRoute>
          } />
          <Route path="/game-mode" element={
            <LazyRoute>
              <GameModePage />
            </LazyRoute>
          } />
        </Route>
      </Route>

      {/* Gamification Routes - Available to all authenticated users */}
      <Route element={<ProtectedRoute />}>
        <Route element={<CandidateLayout />}>
          <Route path="/gamification" element={
            <LazyRoute>
              <GamificationPage />
            </LazyRoute>
          } />
          <Route path="/learning" element={
            <LazyRoute>
              <LearningPage />
            </LazyRoute>
          } />
          <Route path="/learning/progress/:pathId" element={
            <LazyRoute>
              <LearningPage />
            </LazyRoute>
          } />
          <Route path="/learning/certificates" element={
            <LazyRoute>
              <LearningPage />
            </LazyRoute>
          } />
          <Route path="/learning/resources" element={
            <LazyRoute>
              <LearningPage />
            </LazyRoute>
          } />
          <Route path="/simulations" element={
            <LazyRoute>
              <SimulationsPage />
            </LazyRoute>
          } />
          <Route path="/simulations/:id/review" element={
            <LazyRoute>
              <SimulationsPage />
            </LazyRoute>
          } />
          <Route path="/settings" element={
            <LazyRoute>
              <Settings />
            </LazyRoute>
          } />
          <Route path="/settings/profile" element={
            <LazyRoute>
              <Settings />
            </LazyRoute>
          } />
          <Route path="/settings/notifications" element={
            <LazyRoute>
              <Settings />
            </LazyRoute>
          } />
          <Route path="/settings/security" element={
            <LazyRoute>
              <Settings />
            </LazyRoute>
          } />
          <Route path="/profile" element={
            <LazyRoute>
              <Settings />
            </LazyRoute>
          } />
        </Route>
      </Route>

      {/* Protected Interviewer Routes */}
      <Route path="/interviewer" element={
        <ProtectedRoute allowedRoles={['interviewer', 'orgadmin']}>
          <RequireOrganization>
            <InterviewerLayout>
              <LazyRoute>
                <InterviewerDashboard />
              </LazyRoute>
            </InterviewerLayout>
          </RequireOrganization>
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/interviewer/dashboard" replace />} />
        <Route path="dashboard" element={
          <LazyRoute>
            <InterviewerDashboard />
          </LazyRoute>
        } />
        <Route path="tasks" element={
          <LazyRoute>
            <InterviewerDashboard />
          </LazyRoute>
        } />
      </Route>

      {/* Template Routes */}
      <Route path="/templates" element={
        <ProtectedRoute allowedRoles={['interviewer', 'orgadmin']}>
          <RequireOrganization>
            <LazyRoute>
              <TemplateList />
            </LazyRoute>
          </RequireOrganization>
        </ProtectedRoute>
      } />
      <Route path="/templates/create" element={
        <ProtectedRoute allowedRoles={['interviewer', 'orgadmin']}>
          <RequireOrganization>
            <LazyRoute>
              <CreateTemplate />
            </LazyRoute>
          </RequireOrganization>
        </ProtectedRoute>
      } />
      <Route path="/templates/:id" element={
        <ProtectedRoute allowedRoles={['interviewer', 'orgadmin']}>
          <RequireOrganization>
            <LazyRoute>
              <CreateTemplate />
            </LazyRoute>
          </RequireOrganization>
        </ProtectedRoute>
      } />

      {/* Org Admin Routes */}
      <Route path="/org-admin" element={
        <ProtectedRoute allowedRoles={['orgadmin']}>
          <RequireOrganization>
            <LazyRoute>
              <OrgAdminDashboard />
            </LazyRoute>
          </RequireOrganization>
        </ProtectedRoute>
      } />
      <Route path="/org-admin/users" element={
        <ProtectedRoute allowedRoles={['orgadmin']}>
          <RequireOrganization>
            <LazyRoute>
              <OrgAdminUsers />
            </LazyRoute>
          </RequireOrganization>
        </ProtectedRoute>
      } />
      <Route path="/org-admin/settings" element={
        <ProtectedRoute allowedRoles={['orgadmin']}>
          <RequireOrganization>
            <LazyRoute>
              <OrgAdminSettings />
            </LazyRoute>
          </RequireOrganization>
        </ProtectedRoute>
      } />
      <Route path="/org-admin/tasks" element={
        <ProtectedRoute allowedRoles={['orgadmin']}>
          <RequireOrganization>
            <LazyRoute>
              <OrgAdminTasks />
            </LazyRoute>
          </RequireOrganization>
        </ProtectedRoute>
      } />

      {/* Org Admin Billing Route */}
      <Route path="/account/billing" element={
        <ProtectedRoute allowedRoles={['orgadmin']}>
          <RequireOrganization>
            <LazyRoute>
              <BillingAccount />
            </LazyRoute>
          </RequireOrganization>
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['orgadmin', 'admin']}>
          <RequireOrganization>
            <AdminLayout />
          </RequireOrganization>
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={
          <LazyRoute>
            <Dashboard />
          </LazyRoute>
        } />
        <Route path="templates" element={
          <LazyRoute>
            <TemplateList />
          </LazyRoute>
        } />
        <Route path="templates/new" element={
          <LazyRoute>
            <CreateTemplate />
          </LazyRoute>
        } />
        <Route path="candidates" element={
          <LazyRoute>
            <CandidatesManagement />
          </LazyRoute>
        } />
        <Route path="candidates/invite" element={<div>Invite Candidate</div>} />
        <Route path="interviewers" element={
          <LazyRoute>
            <InterviewersManagement />
          </LazyRoute>
        } />
        <Route path="interviewers/invite" element={<div>Invite Interviewer</div>} />
        <Route path="tasks" element={
          <LazyRoute>
            <OrgAdminTasks />
          </LazyRoute>
        } />
        <Route path="tasks/assign" element={
          <LazyRoute>
            <CreateTask />
          </LazyRoute>
        } />
        <Route path="analytics" element={
          <LazyRoute>
            <AnalyticsDashboard />
          </LazyRoute>
        } />
        <Route path="subscription" element={
          <LazyRoute>
            <SubscriptionManagement />
          </LazyRoute>
        } />
        <Route path="settings" element={
          <LazyRoute>
            <OrgAdminSettings />
          </LazyRoute>
        } />
        <Route path="support" element={
          <LazyRoute>
            <EnterpriseSupportPortal />
          </LazyRoute>
        } />
      </Route>

      {/* Super Admin Routes */}
      <Route path="/super-admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <LazyRoute>
            <SuperAdminDashboard />
          </LazyRoute>
        </ProtectedRoute>
      } />

      {/* Shared Protected Routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<CandidateLayout />}>
          <Route path="/support" element={
            <LazyRoute>
              <EnterpriseSupportPortal />
            </LazyRoute>
          } />
          <Route path="/documentation" element={
            <LazyRoute>
              <DocumentationPortalHome />
            </LazyRoute>
          } />
          <Route path="/documentation/:companyId/:fileId" element={
            <LazyRoute>
              <DocumentationFileViewer />
            </LazyRoute>
          } />
        </Route>
      </Route>

      {/* Candidate Directory */}
      <Route path="/candidates" element={
        <ProtectedRoute allowedRoles={['interviewer', 'orgadmin']}>
          <InterviewerLayout>
            <LazyRoute>
              <CandidateDirectory />
            </LazyRoute>
          </InterviewerLayout>
        </ProtectedRoute>
      } />

      {/* New static pages */}
      <Route path="/features" element={
        <LazyRoute>
          <Features />
        </LazyRoute>
      } />
      <Route path="/integrations" element={
        <LazyRoute>
          <Integrations />
        </LazyRoute>
      } />
      <Route path="/docs" element={
        <LazyRoute>
          <Docs />
        </LazyRoute>
      } />
      <Route path="/roadmap" element={
        <LazyRoute>
          <Roadmap />
        </LazyRoute>
      } />
      <Route path="/about" element={
        <LazyRoute>
          <About />
        </LazyRoute>
      } />
      
      {/* Legal Pages */}
      <Route path="/terms" element={
        <LazyRoute>
          <TermsOfService />
        </LazyRoute>
      } />
      <Route path="/privacy" element={
        <LazyRoute>
          <PrivacyPolicy />
        </LazyRoute>
      } />
      <Route path="/support-docs" element={
        <LazyRoute>
          <SupportDocs />
        </LazyRoute>
      } />
      
      <Route path="/404" element={
        <LazyRoute>
          <NotFound />
        </LazyRoute>
      } />
      {/* Catch-all route for 404 */}
      <Route path="*" element={
        <LazyRoute>
          <NotFound />
        </LazyRoute>
      } />
    </Routes>
  );
};

export default AppRoutes;